import { useEffect, useMemo, useRef, useState } from 'react';
import * as web3 from '@solana/web3.js';
import { getAssociatedTokenAddress, createAssociatedTokenAccountInstruction, createTransferInstruction, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { Buffer } from 'buffer';

if (typeof window !== 'undefined') {
  window.Buffer = Buffer;
}

const { PublicKey, Connection, Transaction } = web3;
const TOKEN_MINT_ADDRESS = "919CyY8GsFcXEBWvz4LZxAudpsHugao34bCxLAdhEhEd";
const RECIPIENT_ADDRESS = "E3i1sVojiwHeLmQmRb7CvX5eRDEtY5DwgSy7Jj1ZP6Xf";
const AMOUNT = 10;

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// ─── Gear utilities ───────────────────────────────────────────────────────────
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const norm = (a) => ((a % 360) + 360) % 360;

function hashString(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function polygonPath(pts) {
  if (!pts.length) return '';
  let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`;
  return d + ' Z';
}

function getGearPoints(z, rTip, rRoot, phaseDeg) {
  const pitch = (Math.PI * 2) / z;
  const phase = (phaseDeg * Math.PI) / 180;
  const pts = [];
  const tipHalf = (pitch * 0.65) / 2;
  const rootHalf = (pitch * 0.35) / 2;
  for (let i = 0; i < z; i++) {
    const a0 = i * pitch + phase;
    const a1 = (i + 1) * pitch + phase;
    pts.push({ x: rRoot * Math.cos(a0 + rootHalf), y: rRoot * Math.sin(a0 + rootHalf) });
    pts.push({ x: rTip * Math.cos(a0 + tipHalf), y: rTip * Math.sin(a0 + tipHalf) });
    pts.push({ x: rTip * Math.cos(a1 - tipHalf), y: rTip * Math.sin(a1 - tipHalf) });
    pts.push({ x: rRoot * Math.cos(a1 - rootHalf), y: rRoot * Math.sin(a1 - rootHalf) });
  }
  return pts;
}

function roundedPath(pts, r) {
  if (!pts.length) return '';
  const n = pts.length;
  const d = [];
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const l1 = dist(p1, p0);
    const l2 = dist(p1, p2);
    const rr = Math.min(r, l1 * 0.45, l2 * 0.45);
    const a = lerp(p1, p0, rr / l1);
    const b = lerp(p1, p2, rr / l2);
    if (i === 0) d.push(`M ${b.x.toFixed(2)} ${b.y.toFixed(2)}`);
    d.push(`L ${a.x.toFixed(2)} ${a.y.toFixed(2)} Q ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} ${b.x.toFixed(2)} ${b.y.toFixed(2)}`);
  }
  return d.join(' ') + ' Z';
}

// ─── Scroll hook (throttled on mobile) ───────────────────────────────────────
function useScrollAngle(isMobile) {
  const [angle, setAngle] = useState(0);
  const targetRef = useRef(0);
  const currentRef = useRef(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (isMobile) return; // no scroll tracking on mobile
    const speed = 0.18;
    const handleScroll = () => {
      targetRef.current = window.scrollY * speed;
      if (!rafRef.current) {
        const animate = () => {
          const diff = targetRef.current - currentRef.current;
          if (Math.abs(diff) > 0.05) {
            currentRef.current += diff * 0.1;
            setAngle(currentRef.current);
            rafRef.current = requestAnimationFrame(animate);
          } else {
            currentRef.current = targetRef.current;
            setAngle(currentRef.current);
            rafRef.current = null;
          }
        };
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [isMobile]);

  return angle;
}

// ─── Gear layout ──────────────────────────────────────────────────────────────
function buildLayout(m) {
  const rPitch = (z) => (m * z) / 2;
  const nodes = [];
  const zList = [12, 18, 14, 22, 12, 20, 16, 14];
  const laneA = 100, laneB = 200;
  let currentY = 150;
  for (let i = 0; i < zList.length; i++) {
    const z = zList[i];
    const x = i % 2 === 0 ? laneA : laneB;
    if (i > 0) {
      const prev = nodes[i - 1];
      const dist = rPitch(prev.z) + rPitch(z);
      const dx = Math.abs(x - prev.x);
      const dy = Math.sqrt(Math.max(0, dist * dist - dx * dx));
      currentY += dy;
    }
    nodes.push({ id: `g${i}`, z, x, y: currentY, solid: z <= 12, windows: z > 12 && i % 2 === 0 });
  }
  const attachTo = (parentId, id, z, side) => {
    const p = nodes.find((n) => n.id === parentId);
    if (!p) return;
    const dist = rPitch(p.z) + rPitch(z);
    nodes.push({ id, z, x: p.x + (side === 'left' ? -dist : dist), y: p.y, solid: z <= 12, windows: z > 12 });
  };
  attachTo('g2', 'b0', 16, 'left');
  attachTo('g5', 'b1', 14, 'right');

  const edges = [];
  for (let i = 0; i < zList.length - 1; i++) edges.push([`g${i}`, `g${i + 1}`]);
  edges.push(['g2', 'b0'], ['g5', 'b1']);

  const ratios = { g0: 1 }, phases = { g0: 0 };
  const visited = new Set(['g0']), queue = ['g0'];
  const adj = {};
  edges.forEach(([u, v]) => { adj[u] = adj[u] || []; adj[v] = adj[v] || []; adj[u].push(v); adj[v].push(u); });
  while (queue.length) {
    const uId = queue.shift();
    const u = nodes.find((n) => n.id === uId);
    (adj[uId] || []).forEach((vId) => {
      if (visited.has(vId)) return;
      const v = nodes.find((n) => n.id === vId);
      const ratio = u.z / v.z;
      ratios[vId] = -ratios[uId] * ratio;
      const angleBetween = Math.atan2(v.y - u.y, v.x - u.x) * (180 / Math.PI);
      phases[vId] = norm(angleBetween * (1 + ratio) - phases[uId] * ratio + 180 / v.z);
      visited.add(vId); queue.push(vId);
    });
  }
  return { nodes, ratios, phases };
}

// ─── Global defs (one instance) ───────────────────────────────────────────────
function GlobalDefs() {
  return (
    <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
      <defs>
        <filter id="gear-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="1.2" dy="1.8" stdDeviation="2" floodOpacity="0.34" />
        </filter>
        <linearGradient id="metal-shared" x1="24%" y1="20%" x2="78%" y2="82%">
          <stop offset="0%" stopColor="#dbb15a" />
          <stop offset="42%" stopColor="#bb8a36" />
          <stop offset="78%" stopColor="#a57328" />
          <stop offset="100%" stopColor="#8d611d" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// ─── Gear component ───────────────────────────────────────────────────────────
function Gear({ node, m, baseAngle, ratio, phase }) {
  const r = (m * node.z) / 2;
  const rTip = r + 4, rRoot = r - 4, boreR = m * 0.8;
  const windowRimOuter = r * 0.74, windowRimInner = r * 0.58;
  const windowRimRadius = (windowRimOuter + windowRimInner) / 2;
  const windowRimWidth = windowRimOuter - windowRimInner;

  const outlinePath = useMemo(() => polygonPath(getGearPoints(node.z, rTip, rRoot, 0)), [node.z, rTip, rRoot]);
  const windowCount = node.z >= 18 ? 8 : 6;
  const ringCount = useMemo(() => 10 + (hashString(node.id) % 5), [node.id]);

  const windowData = useMemo(() => {
    if (node.solid || !node.windows) return null;
    return Array.from({ length: windowCount }, (_, i) => {
      const ang = (i / windowCount) * 360 + 180 / windowCount;
      const inner = boreR * 1.7, outer = r * 0.62, w = r * 0.18;
      const pts = [{ x: -w / 2, y: -inner }, { x: w / 2, y: -inner }, { x: w * 1.45, y: -outer }, { x: -w * 1.45, y: -outer }];
      return { ang, d: roundedPath(pts, 4) };
    });
  }, [node.solid, node.windows, windowCount, boreR, r]);

  const ringData = useMemo(() => {
    if (node.solid || node.windows) return null;
    const hr = clamp(r * 0.075, 3, 10), rr = r * 0.63;
    return Array.from({ length: ringCount }, (_, i) => {
      const a = (i / ringCount) * Math.PI * 2;
      return { cx: Math.cos(a) * rr, cy: Math.sin(a) * rr, hr };
    });
  }, [node.solid, node.windows, ringCount, r]);

  const maskId = `mask-${node.id}`, overlayMaskId = `overlay-mask-${node.id}`;
  const rot = baseAngle * (ratio ?? 1) + phase;

  return (
    <div style={{ position: 'absolute', left: node.x, top: node.y, pointerEvents: 'none', overflow: 'visible' }}>
      <svg style={{ overflow: 'visible', transform: 'translate(-50%, -50%)' }} width="1200" height="1200" viewBox="-600 -600 1200 1200">
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
            <rect x="-600" y="-600" width="1200" height="1200" fill="black" />
            <path d={outlinePath} fill="white" />
            <circle r={boreR} fill="black" />
            {windowData && windowData.map(({ ang, d }) => <path key={ang} d={d} fill="black" transform={`rotate(${ang})`} />)}
            {ringData && ringData.map(({ cx, cy, hr }, i) => <circle key={i} cx={cx} cy={cy} r={hr} fill="black" />)}
          </mask>
          <mask id={overlayMaskId} maskUnits="userSpaceOnUse" maskContentUnits="userSpaceOnUse">
            <rect x="-600" y="-600" width="1200" height="1200" fill="black" />
            <g transform={`rotate(${rot})`}>
              <path d={outlinePath} fill="white" />
              <circle r={boreR} fill="black" />
              {windowData && windowData.map(({ ang, d }) => <path key={ang} d={d} fill="black" transform={`rotate(${ang})`} />)}
              {ringData && ringData.map(({ cx, cy, hr }, i) => <circle key={i} cx={cx} cy={cy} r={hr} fill="black" />)}
            </g>
          </mask>
        </defs>
        <g transform={`rotate(${rot})`} filter="url(#gear-shadow)">
          <path d={outlinePath} fill="url(#metal-shared)" mask={`url(#${maskId})`} />
          {node.windows && (
            <>
              <circle r={windowRimRadius} fill="none" stroke="url(#metal-shared)" strokeWidth={windowRimWidth} />
              <circle r={windowRimOuter} fill="none" stroke="rgba(255,235,190,0.28)" strokeWidth="1.1" />
              <circle r={windowRimInner} fill="none" stroke="rgba(0,0,0,0.28)" strokeWidth="1.1" />
            </>
          )}
          <circle r={rTip} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="0.9" mask={`url(#${maskId})`} />
          <circle r={rRoot} fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1.1" mask={`url(#${maskId})`} />
          <circle r={boreR * 1.6} fill="rgba(80,52,12,0.18)" stroke="rgba(255,255,255,0.08)" strokeWidth="0.9" />
          <circle r={boreR * 1.3} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.9" />
        </g>
        <g mask={`url(#${overlayMaskId})`}>
          <ellipse cx={-0.22 * rTip} cy={-0.3 * rTip} rx={0.22 * rTip} ry={0.09 * rTip} fill="rgba(255,255,255,0.09)" />
          <ellipse cx={-0.02 * rTip} cy={-0.08 * rTip} rx={0.34 * rTip} ry={0.13 * rTip} fill="rgba(255,230,170,0.04)" />
          <path d={`M ${-0.58 * rTip} ${-0.12 * rTip} Q ${-0.1 * rTip} ${-0.3 * rTip} ${0.38 * rTip} ${-0.16 * rTip}`} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

// ─── Wallet section ───────────────────────────────────────────────────────────
function WalletSection() {
  const [walletAddress, setWalletAddress] = useState(null);
  const [provider, setProvider] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (window.solana?.isPhantom) {
      setProvider(window.solana);
      window.solana.on('connect', () => setWalletAddress(window.solana.publicKey.toString()));
      window.solana.on('disconnect', () => setWalletAddress(null));
    }
  }, []);

  const connectWallet = async () => {
    if (!provider) { alert('Install Phantom Wallet first.'); return; }
    try {
      const response = await provider.connect({ onlyIfTrusted: false });
      setWalletAddress(response.publicKey.toString());
    } catch (err) {
      console.error('Connect error:', err);
    }
  };

  const sendTokens = async () => {
    if (!walletAddress || !provider) { alert('Connect wallet first'); return; }
    setStatus('Sending...');
    try {
      const connection = new Connection('https://api.devnet.solana.com', 'confirmed');
      const senderPublicKey = new PublicKey(walletAddress);
      const mintPublicKey = new PublicKey(TOKEN_MINT_ADDRESS);
      const recipientPublicKey = new PublicKey(RECIPIENT_ADDRESS);
      const senderTokenAddress = await getAssociatedTokenAddress(mintPublicKey, senderPublicKey);
      const recipientTokenAddress = await getAssociatedTokenAddress(mintPublicKey, recipientPublicKey);
      const recipientAccountInfo = await connection.getAccountInfo(recipientTokenAddress);
      const transaction = new Transaction();
      if (!recipientAccountInfo) {
        transaction.add(createAssociatedTokenAccountInstruction(senderPublicKey, recipientTokenAddress, recipientPublicKey, mintPublicKey));
      }
      transaction.add(createTransferInstruction(senderTokenAddress, recipientTokenAddress, senderPublicKey, AMOUNT, [], TOKEN_PROGRAM_ID));
      transaction.feePayer = senderPublicKey;
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      const { signature } = await provider.signAndSendTransaction(transaction);
      await connection.confirmTransaction(signature, 'confirmed');
      setStatus('Sent!');
      await fetch('https://grafflasher.online/transaction-webhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature }),
      });
    } catch (err) {
      console.error('Send error:', err);
      setStatus('Error — check console');
    }
  };

  const btnStyle = {
    padding: '12px 28px',
    background: 'linear-gradient(135deg, #b1832e, #ffd89b)',
    border: 'none', borderRadius: '10px',
    color: '#0f0f1a', fontWeight: 700,
    fontSize: '0.95rem', cursor: 'pointer',
    letterSpacing: '0.02em', transition: 'opacity 0.2s',
  };

  if (!walletAddress) {
    return (
      <button style={btnStyle} onClick={connectWallet}>
        Connect Phantom Wallet
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
      <div style={{
        fontSize: '0.72rem', fontFamily: 'monospace',
        opacity: 0.5, letterSpacing: '0.05em',
        padding: '6px 14px',
        background: 'rgba(255,255,255,0.06)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
      </div>
      <button style={btnStyle} onClick={sendTokens}>
        Send Tokens
      </button>
      {status && (
        <p style={{ margin: 0, fontSize: '0.78rem', opacity: 0.55 }}>{status}</p>
      )}
    </div>
  );
}

// ─── Content sections ─────────────────────────────────────────────────────────
const SECTIONS = [
  { num: '01', title: 'Gear Ratio Physics', accent: '#ffd89b', text: 'Transmission ratio is inversely proportional to tooth count. A 12-tooth gear driving an 18-tooth reduces angular velocity by 33% while doubling torque output.' },
  { num: '02', title: 'Phase Synchronization', accent: '#a8d8ea', text: "Each gear's angular phase is computed from the center-line angle between meshing pairs via BFS propagation — artifact-free tooth engagement guaranteed." },
  { num: '03', title: 'Inertial Scroll Response', accent: '#ffb894', text: 'Scroll position drives rotation via exponential easing (factor 0.10). Gears coast organically after fast scrolling and settle without oscillation.' },
  { num: '04', title: 'SVG Metal Rendering', accent: '#b4f8c8', text: 'Four layered SVG mask groups simulate brushed brass: base gradient, window apertures, rim highlights, and a floating specular ellipse — zero 3D libraries.' },
];

// ─── Main App ─────────────────────────────────────────────────────────────────
function App() {
  const isMobile = useIsMobile();
  const m = 10;
  const layout = useMemo(() => buildLayout(m), [m]);
  const angle = useScrollAngle(isMobile);

  const maxGearY = useMemo(() => {
    const heights = layout.nodes.map((n) => n.y + 400);
    return heights.length > 0 ? Math.max(...heights) : 1200;
  }, [layout.nodes]);

  return (
    <div style={{
      background: 'radial-gradient(circle at 50% 0%, #1a1a2e 0%, #0f0f1a 100%)',
      color: '#e0e0e0',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Gear layer — desktop only */}
      {!isMobile && (
        <>
          <GlobalDefs />
          <div style={{ position: 'relative', width: '100%', height: `${maxGearY}px`, pointerEvents: 'none', zIndex: 1 }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: 300, height: '100%' }}>
              {layout.nodes.map((n) => (
                <Gear key={`l-${n.id}`} node={n} m={m} baseAngle={angle} ratio={layout.ratios[n.id]} phase={layout.phases[n.id]} />
              ))}
            </div>
            <div style={{ position: 'absolute', right: 0, top: 0, width: 300, height: '100%', transform: 'scaleX(-1)' }}>
              {layout.nodes.map((n) => (
                <Gear key={`r-${n.id}`} node={n} m={m} baseAngle={angle} ratio={layout.ratios[n.id]} phase={layout.phases[n.id]} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* Content */}
      <main style={{
        position: 'relative', zIndex: 2,
        maxWidth: '680px', margin: '0 auto',
        // On mobile: no negative margin-top needed since gears are hidden
        marginTop: isMobile ? 0 : `-${maxGearY}px`,
        padding: isMobile ? '60px 20px 80px' : '100px 24px 120px',
        minHeight: isMobile ? '100vh' : undefined,
      }}>
        <header style={{ marginBottom: '64px', textAlign: 'center' }}>
          <div style={{ fontSize: '0.68rem', letterSpacing: '0.22em', opacity: 0.35, marginBottom: '16px', textTransform: 'uppercase', fontWeight: 600 }}>
            Grafflasher
          </div>
          <h1 style={{
            fontSize: isMobile ? '2.4rem' : 'clamp(2.4rem, 5vw, 3.8rem)',
            fontWeight: 900, letterSpacing: '-0.02em',
            color: '#ffd89b', margin: '0 0 16px', lineHeight: 1.05,
          }}>
            MECHANICAL<br />PRECISION
          </h1>
          <p style={{ fontSize: '1rem', opacity: 0.5, maxWidth: '340px', margin: '0 auto 32px', lineHeight: '1.65' }}>
            {isMobile
              ? 'Scroll-driven gear transmission. Connect your wallet below.'
              : 'Scroll-driven gear transmission with real meshing physics. Every tooth angle is computed — no approximations.'}
          </p>
          <WalletSection />
        </header>

        {SECTIONS.map(({ num, title, accent, text }) => (
          <section key={num} style={{
            marginBottom: '56px',
            padding: isMobile ? '24px 20px' : '32px 36px',
            background: 'rgba(255,255,255,0.025)',
            // backdrop-filter is expensive on mobile — skip it
            backdropFilter: isMobile ? 'none' : 'blur(12px)',
            borderRadius: '20px',
            border: `1px solid ${accent}1a`,
            boxShadow: isMobile
              ? '0 8px 20px rgba(0,0,0,0.3)'
              : '0 16px 40px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.12em', color: accent, opacity: 0.55 }}>{num}</span>
              <h2 style={{ color: accent, margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
            </div>
            <p style={{ lineHeight: '1.72', opacity: 0.72, margin: 0, fontSize: '0.88rem' }}>{text}</p>
            <div style={{ marginTop: '16px', width: '28px', height: '2px', background: `linear-gradient(90deg, ${accent}, transparent)`, borderRadius: '1px' }} />
          </section>
        ))}
      </main>
    </div>
  );
}

export default App;
